/**
 * Animated fire textures.
 *
 * Vanilla fire is two 16x512 sheets — `fire_0.png` and `fire_1.png` — each a
 * vertical strip of 32 animation frames. Every fire block picks one of the two
 * sheets (the blockstate lists fire_floor0/fire_floor1) and steps through its
 * frames at one frame per game tick (20 fps).
 *
 * The block atlas can only hold one 16x16 still per tile, which is how fire
 * ended up frozen on frame 0, so the strips live in their own sampler2DArray
 * (same pattern as the water/lava frames in liquid.ts): layer index =
 * sheet * frames + frame.
 *
 * The sheets shipped in some asset dumps are plain RGB with the background
 * blacked out instead of transparent, so frames whose source has no alpha get
 * an alpha keyed from luminance; sheets that do carry alpha are used as-is.
 * Missing files fall back to a procedural flame so the engine still runs.
 */

import * as THREE from 'three';
import { mulberry32 } from '../noise';

export interface FireTextures {
  /** Layers: [fire_0 frames..., fire_1 frames...]. */
  texture: THREE.DataArrayTexture;
  /** Frames per sheet. */
  frames: number;
  /** Sheet count (2: fire_0, fire_1). */
  sheets: number;
}

/** Frame edge length in texels (vanilla block textures are 16x16). */
const SIZE = 16;
const LAYER_BYTES = SIZE * SIZE * 4;
// sheet 2 is the nether portal swirl (32 frames too), drawn by the portal panes
const SHEET_NAMES = ['fire_0', 'fire_1', 'nether_portal', 'soul_fire_0', 'soul_fire_1'];
const TAU = Math.PI * 2;

async function loadImage(name: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = `mc/${name}.png`; // relative — works under vite's './' base
  try {
    await img.decode();
    return img;
  } catch {
    return null; // not extracted — procedural fallback
  }
}

interface Sheet {
  /** frames * LAYER_BYTES, rows top-down. */
  data: Uint8Array;
  frames: number;
}

/** Slice a vertical 16xN animation strip into 16x16 RGBA frames. */
function decodeSheet(img: HTMLImageElement): Sheet | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const frames = w > 0 ? Math.floor(h / w) : 0;
  if (frames < 1) return null;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  const src = ctx.getImageData(0, 0, w, h).data;

  // Opaque source (RGB dump with a black backdrop): key alpha from luminance.
  let hasAlpha = false;
  for (let i = 3; i < src.length; i += 4) {
    if (src[i] !== 255) { hasAlpha = true; break; }
  }

  const data = new Uint8Array(frames * LAYER_BYTES);
  for (let f = 0; f < frames; f++) {
    for (let y = 0; y < SIZE; y++) {
      // Texel centres, so up/down-sampling picks a stable source texel.
      const sy = f * w + Math.min(w - 1, Math.floor(((y + 0.5) * w) / SIZE));
      for (let x = 0; x < SIZE; x++) {
        const sx = Math.min(w - 1, Math.floor(((x + 0.5) * w) / SIZE));
        const s = (sy * w + sx) * 4;
        const d = f * LAYER_BYTES + (y * SIZE + x) * 4;
        const r = src[s], g = src[s + 1], b = src[s + 2];
        data[d] = r;
        data[d + 1] = g;
        data[d + 2] = b;
        data[d + 3] = hasAlpha ? src[s + 3] : (Math.max(r, g, b) > 8 ? 255 : 0);
      }
    }
  }
  return { data, frames };
}

/** Reasonable-looking flame when no MC asset is present: a column height field
 *  that wobbles across frames, lit from the bottom. Loops seamlessly. */
function proceduralSheet(seed: number): Sheet {
  const rnd = mulberry32(seed);
  const FRAMES = 16;
  const phases = new Float32Array(SIZE);
  const rates = new Float32Array(SIZE);
  for (let x = 0; x < SIZE; x++) {
    phases[x] = rnd() * TAU;
    // integer cycles per loop so the last frame flows into the first
    rates[x] = (rnd() < 0.5 ? -1 : 1) * (1 + ((rnd() * 2) | 0));
  }
  const data = new Uint8Array(FRAMES * LAYER_BYTES);
  for (let f = 0; f < FRAMES; f++) {
    const t = f / FRAMES;
    for (let x = 0; x < SIZE; x++) {
      const wobble = Math.sin(TAU * rates[x] * t + phases[x]);
      const top = Math.max(2, Math.min(SIZE - 2, 5 + wobble * 2.5 + rnd() * 0.5));
      for (let y = 0; y < SIZE; y++) {
        const up = (SIZE - 1 - y) / top; // 0 at the flame base, 1 at the tip
        const d = f * LAYER_BYTES + (y * SIZE + x) * 4;
        if (up > 1) continue; // above the flame: transparent
        // base hot white-yellow, tip deep orange
        const heat = 1 - up;
        data[d] = 190 + heat * 65;
        data[d + 1] = 60 + heat * 175;
        data[d + 2] = 10 + heat * 120;
        data[d + 3] = 255;
      }
    }
  }
  return { data, frames: FRAMES };
}

export async function buildFireTextures(): Promise<FireTextures> {
  const loaded = await Promise.all(SHEET_NAMES.map((n) => loadImage(n)));
  const sheets: Sheet[] = loaded.map((img, i) =>
    (img && decodeSheet(img)) || proceduralSheet(0x51e4 + i * 977)
  );

  // Both sheets must have the same frame count: the shader indexes
  // sheet * frames + frame, so a short sheet would shift the other's frames.
  const frames = Math.min(...sheets.map((s) => s.frames));
  const data = new Uint8Array(sheets.length * frames * LAYER_BYTES);
  sheets.forEach((s, i) => {
    data.set(s.data.subarray(0, frames * LAYER_BYTES), i * frames * LAYER_BYTES);
  });

  const texture = new THREE.DataArrayTexture(data, SIZE, SIZE, sheets.length * frames);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.colorSpace = THREE.SRGBColorSpace; // -> SRGB8_ALPHA8, decoded on sample
  // Clamp, not repeat: a flame plate maps uv 0..1 across one frame, so there is
  // nothing to wrap — and with repeat the uv=1.0 edge blends the last texel
  // column back into the first one, which draws a faint line around each plate
  // (clearly visible against the sky).
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // No mips, like vanilla: fire renders in the plain cutout layer
  // (cutout_mipped is for leaves). Mip levels average the frame's transparent
  // gaps into the flame, and at a grazing angle a flame plane is a hairline —
  // those averaged texels turned every edge-on plane into a solid bright
  // streak across the view.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  return { texture, frames, sheets: sheets.length };
}
